/**
 * 统计面板组件
 * 显示实时统计信息和图表
 */

import React from 'react';
import { motion } from 'framer-motion';
import { 
  Database, 
  Key, 
  Activity, 
  FileText, 
  Clock
} from 'lucide-react';
import { clsx } from 'clsx';
import { formatNumber, formatRelativeTime } from '../utils/format';
import { useRealtimeStats } from '../hooks/useApi';
import Card from './ui/Card';
import Loading from './ui/Loading';


interface StatsPanelProps {
  className?: string;
  refreshInterval?: number;
}

const StatsPanel: React.FC<StatsPanelProps> = ({
  className,
  refreshInterval = 5000
}) => {
  const { stats, loading, error } = useRealtimeStats(refreshInterval);

  if (loading && !stats) {
    return (
      <Card className={className}>
        <Loading text="加载统计信息..." />
      </Card>
    );
  }

  if (error) {
    return (
      <Card className={className}>
        <div className="text-center py-8">
          <Activity className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">加载失败</h3>
          <p className="text-red-600 text-sm">{error}</p>
        </div>
      </Card>
    );
  }

  if (!stats) {
    return (
      <Card className={className}>
        <div className="text-center py-8">
          <Database className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">暂无统计数据</h3>
          <p className="text-gray-500 text-sm">请稍后再试</p>
        </div>
      </Card>
    );
  }

  const { overview, topKeys, contentTypes, timestamp } = stats;

  // 统计卡片数据
  const statCards = [
    {
      title: '总请求数',
      value: overview.totalRequests,
      icon: Database,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100'
    },
    {
      title: '键值对数',
      value: overview.totalKeyValues,
      icon: Key,
      color: 'text-green-600',
      bgColor: 'bg-green-100'
    },
    {
      title: '近期活动',
      value: overview.recentActivity,
      icon: Activity,
      color: 'text-purple-600',
      bgColor: 'bg-purple-100'
    }
  ];

  // 内容类型数据
  const contentTypeEntries = Object.entries(contentTypes);
  const totalContentTypes = contentTypeEntries.reduce((sum, [, count]) => sum + count, 0);

  return (
    <div className={clsx('space-y-6', className)}>
      {/* 概览统计 */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {statCards.map((stat, index) => (
          <motion.div
            key={stat.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
          >
            <Card hover className="h-full">
              <div className="flex items-center">
                <div className={clsx('p-3 rounded-lg', stat.bgColor)}>
                  <stat.icon className={clsx('w-6 h-6', stat.color)} />
                </div>
                <div className="ml-4 flex-1">
                  <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatNumber(stat.value)}
                  </p>
                </div>
              </div>
            </Card>
          </motion.div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* 热门键名 */}
        <Card title="热门键名" subtitle="使用频率最高的键名">
          <div className="space-y-3">
            {topKeys.length > 0 ? (
              topKeys.slice(0, 8).map((item, index) => {
                const percentage = overview.totalKeyValues > 0 
                  ? (item.count / overview.totalKeyValues) * 100 
                  : 0;
                
                return (
                  <motion.div
                    key={item.key}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="flex items-center justify-between"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2">
                        <span className="text-sm font-medium text-gray-900 truncate">
                          {item.key}
                        </span>
                        <span className="text-xs text-gray-500">
                          {percentage.toFixed(1)}%
                        </span>
                      </div>
                      <div className="mt-1 bg-gray-200 rounded-full h-2">
                        <motion.div
                          className="bg-primary-600 h-2 rounded-full"
                          initial={{ width: 0 }}
                          animate={{ width: `${percentage}%` }}
                          transition={{ delay: index * 0.05 + 0.2, duration: 0.5 }}
                        />
                      </div>
                    </div>
                    <div className="ml-4 text-sm font-medium text-gray-600">
                      {formatNumber(item.count)}
                    </div>
                  </motion.div>
                );
              })
            ) : (
              <div className="text-center py-8">
                <Key className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-500">暂无数据</p>
              </div>
            )}
          </div>
        </Card>

        {/* 内容类型分布 */}
        <Card title="内容类型分布" subtitle="不同数据格式的占比">
          <div className="space-y-3">
            {contentTypeEntries.length > 0 ? (
              contentTypeEntries.map(([type, count], index) => {
                const percentage = totalContentTypes > 0 
                  ? (count / totalContentTypes) * 100 
                  : 0;
                
                const formatType = (type: string) => {
                  const typeMap: Record<string, string> = {
                    'application/json': 'JSON',
                    'application/xml': 'XML',
                    'text/xml': 'XML',
                    'text/plain': 'Text'
                  };
                  return typeMap[type] || type;
                };

                return (
                  <motion.div
                    key={type}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="flex items-center justify-between"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2">
                        <span className="text-sm font-medium text-gray-900">
                          {formatType(type)}
                        </span>
                        <span className="text-xs text-gray-500">
                          {percentage.toFixed(1)}%
                        </span>
                      </div>
                      <div className="mt-1 bg-gray-200 rounded-full h-2">
                        <motion.div
                          className="bg-green-600 h-2 rounded-full"
                          initial={{ width: 0 }}
                          animate={{ width: `${percentage}%` }}
                          transition={{ delay: index * 0.05 + 0.2, duration: 0.5 }}
                        />
                      </div>
                    </div>
                    <div className="ml-4 text-sm font-medium text-gray-600">
                      {formatNumber(count)}
                    </div>
                  </motion.div>
                );
              })
            ) : (
              <div className="text-center py-8">
                <FileText className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-500">暂无数据</p>
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* 更新时间 */}
      <Card padding="sm">
        <div className="flex items-center justify-between text-sm text-gray-500">
          <div className="flex items-center space-x-2">
            <Clock className="w-4 h-4" />
            <span>最后更新: {formatRelativeTime(timestamp)}</span>
          </div>
          <div className="flex items-center space-x-1">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
            <span>实时更新</span>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default StatsPanel;